import { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { PurchaseProductDetails } from '@ancon/wildcat-types'
import useTranslation from 'next-translate/useTranslation'
import clsx from 'clsx'

import Button from '../../../app/components/Button'
import HeadingText from '../../../app/components/HeadingText'
import { ConfigureProductUpsellGroupFormState } from '../../types'
import useAppSelector from '../../../../store/hooks/useAppSelector'
import {
  productDetailsSelector,
  productsDetailsProductNameSelector,
  productsDetailsUpsellGroupNameSelector,
  productsDetailsUpsellGroupProductsSelector,
} from '../../store/productSelectors'
import { AppTestIds } from '../../../app/constants'
import BodyText from '../../../app/components/BodyText'
import getSelectedUpsellCount from '../../utils/getSelectedUpsellCount'

import styles from './ConfigureUpsellGroupForm.module.scss'
import UpsellProductCard from './UpsellProductCard'

function getInitialUpsellGroupState(productDetails: PurchaseProductDetails) {
  const { upsellGroups } = productDetails

  if (!!upsellGroups.length && !!upsellGroups[0].products.length) {
    const { products, id } = upsellGroups[0]

    return {
      selectedUpsellGroups: products.map(product => ({
        variantId: product.variantId,
        selectedCount: 0,
      })),
      upsellGroupId: id,
    }
  }

  return {
    selectedUpsellGroups: [],
    upsellGroupId: undefined,
  }
}

type ConfigureUpsellGroupFormProps = {
  onSubmit: (data: ConfigureProductUpsellGroupFormState) => void
}

export default function ConfigureUpsellGroupForm({
  onSubmit,
}: ConfigureUpsellGroupFormProps) {
  const { t } = useTranslation('common')
  const productDetails = useAppSelector(productDetailsSelector)
  const upsellGroupProducts = useAppSelector(
    productsDetailsUpsellGroupProductsSelector,
  )
  const upsellGroupName = useAppSelector(productsDetailsUpsellGroupNameSelector)
  const productName = useAppSelector(productsDetailsProductNameSelector)
  const contentContainerRef = useRef<HTMLDivElement>(null)
  const [showHeader, setShowHeader] = useState(false)

  const form = useForm<ConfigureProductUpsellGroupFormState>({
    defaultValues: {
      ...getInitialUpsellGroupState(productDetails!),
    },
  })
  const { handleSubmit, watch } = form

  const selectedUpsellCount = getSelectedUpsellCount(
    watch('selectedUpsellGroups'),
  )

  const continueLabel = selectedUpsellCount
    ? t('upsellModal.continueWith', { count: selectedUpsellCount })
    : t('upsellModal.continueWithout')

  useEffect(() => {
    const contentContainer = contentContainerRef.current

    function handleScroll() {
      if (contentContainer) {
        const scrollTop = contentContainer.scrollTop ?? 0
        setShowHeader(scrollTop > 80)
      }
    }

    if (contentContainer) {
      contentContainer.addEventListener('scroll', handleScroll)
    }

    return () => {
      if (contentContainer) {
        contentContainer.removeEventListener('scroll', handleScroll)
      }
    }
  }, [])

  const title =
    upsellGroupName || t('components.productDetailsModal.commonAdditions')

  return (
    <FormProvider
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...form}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className={styles.container}
          ref={contentContainerRef}
          data-testid={AppTestIds.Order.Modal.ProductUpsellModal.Container}
        >
          <div className={styles.topContainer}>
            <div
              className={clsx(styles.floatingHeader, {
                [styles.show]: showHeader,
              })}
            >
              <HeadingText as="h2">{title}</HeadingText>
            </div>

            <span className={styles.addedTag}>
              {t('upsellModal.readyToAdd')}
            </span>
            <HeadingText as="h2">{title}</HeadingText>
            <BodyText as="p" color="body-1" className={styles.subtitle}>
              {t('upsellModal.willBeAdded', { productName })}
            </BodyText>

            <div className={styles.upsellListing}>
              {upsellGroupProducts.map((upsellProduct, i) => (
                <UpsellProductCard
                  key={upsellProduct.variantId}
                  upsellProduct={upsellProduct}
                  index={i}
                />
              ))}
            </div>
          </div>

          <div className={styles.footerContainer}>
            <Button
              type="submit"
              variant={selectedUpsellCount ? 'primary' : 'secondary'}
              data-testid={
                AppTestIds.Order.Modal.ProductUpsellModal.ContinueButton
              }
            >
              {continueLabel}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
